import axios from 'axios';

/**
 * PhonePe Direct Integration Service
 * Handles PhonePe payments directly from frontend without backend
 * 
 * This service implements the complete PhonePe payment flow:
 * 1. Generate Authorization Token
 * 2. Create Payment
 * 3. Check Payment Status
 */

// Check if environment variables are available
export const checkPhonePeConfiguration = () => {
  const errors = [];
  
  if (!import.meta.env.VITE_PHONEPE_CLIENT_ID) {
    errors.push('VITE_PHONEPE_CLIENT_ID is not found in environment variables');
  }
  
  if (!import.meta.env.VITE_PHONEPE_CLIENT_SECRET) {
    errors.push('VITE_PHONEPE_CLIENT_SECRET is not found in environment variables');
  }
  
  return {
    isValid: errors.length === 0,
    errors: errors,
    errorMessage: errors.length > 0 
      ? `PhonePe Configuration Error:\n${errors.join('\n')}\n\nPlease check your .env file and ensure the following variables are set:\n- VITE_PHONEPE_CLIENT_ID\n- VITE_PHONEPE_CLIENT_SECRET`
      : null
  };
};

// PhonePe Configuration
const PHONEPE_CONFIG = {
  CLIENT_ID: import.meta.env.VITE_PHONEPE_CLIENT_ID,
  CLIENT_SECRET: import.meta.env.VITE_PHONEPE_CLIENT_SECRET,
  CLIENT_VERSION: 1,
  
  // API Endpoints - Use Cloudflare Workers in production, Vite proxy in development
  AUTH_URL: import.meta.env.PROD 
    ? '/api/phonepe/auth' 
    : '/apis/identity-manager/v1/oauth/token', // Production API
  PAYMENT_URL: import.meta.env.PROD 
    ? '/api/phonepe/payment' 
    : '/apis/pg/checkout/v2/pay', // Production API
  STATUS_URL: import.meta.env.PROD 
    ? '/api/phonepe/status' 
    : '/apis/pg/checkout/v2/order', // Production API
  
  // Redirect URLs
  REDIRECT_URL: `${window.location.origin}/donation-success`,
  CALLBACK_URL: `${window.location.origin}/api/phonepe/callback`,
};

// Store authorization token
let authToken = null;
let tokenExpiry = null;

/**
 * Generate Authorization Token using Axios
 * This is the first step in PhonePe integration
 * @returns {Promise<Object>} Authorization response
 */
export const generateAuthToken = async () => {
  try {
    console.log('🔐 [PhonePe] Starting authentication token generation...');
    console.log('🔐 [PhonePe] Environment:', import.meta.env.PROD ? 'Production' : 'Development');
    console.log('🔐 [PhonePe] Using endpoint:', PHONEPE_CONFIG.AUTH_URL);
    console.log('🔐 [PhonePe] Environment check:', {
      CLIENT_ID: PHONEPE_CONFIG.CLIENT_ID ? '✅ Present' : '❌ Missing',
      CLIENT_SECRET: PHONEPE_CONFIG.CLIENT_SECRET ? '✅ Present' : '❌ Missing',
      AUTH_URL: PHONEPE_CONFIG.AUTH_URL
    });

    // Check if we have valid credentials
    if (!PHONEPE_CONFIG.CLIENT_ID || !PHONEPE_CONFIG.CLIENT_SECRET) {
      const error = 'Missing PhonePe credentials';
      console.error('❌ [PhonePe] Authentication failed:', error);
      throw new Error(error);
    }

    // Check if we have a valid token
    if (authToken && tokenExpiry && Date.now() < tokenExpiry * 1000) {
      console.log('[PhonePe] Using existing valid token');
      return {
        success: true,
        access_token: authToken,
        expires_at: tokenExpiry,
        token_type: 'O-Bearer'
      };
    }

    // Prepare request headers
    const requestHeaders = {
      "Content-Type": "application/x-www-form-urlencoded"
    };

    // Prepare request body
    const requestBodyJson = {
      "client_version": PHONEPE_CONFIG.CLIENT_VERSION,
      "grant_type": "client_credentials",
      "client_id": PHONEPE_CONFIG.CLIENT_ID,
      "client_secret": PHONEPE_CONFIG.CLIENT_SECRET
    };

    const requestBody = new URLSearchParams(requestBodyJson).toString();

    const options = {
      method: 'POST',
      url: PHONEPE_CONFIG.AUTH_URL,
      headers: requestHeaders,
      data: requestBody
    };

    console.log('[PhonePe] Making token request to:', PHONEPE_CONFIG.AUTH_URL);
    console.log('[PhonePe] Request options:', {
      method: options.method,
      url: options.url,
      headers: options.headers,
      data: '***form data***'
    });

    // Make authorization request using axios
    const response = await axios.request(options);

    console.log('[PhonePe] Token response status:', response.status);
    console.log('[PhonePe] Token response headers:', response.headers);
    console.log('[PhonePe] Token response data:', response.data);

    const responseData = response.data;

    // Handle the response according to PhonePe API format
    if (!responseData.access_token) {
      throw new Error('No access token received from PhonePe');
    }

    // Store token and expiry
    authToken = responseData.access_token;
    tokenExpiry = responseData.expires_at;

    console.log('[PhonePe] ✅ Authorization token generated successfully!');
    console.log('[PhonePe] Token details:', {
      access_token: responseData.access_token.substring(0, 50) + '...',
      token_type: responseData.token_type,
      expires_at: new Date(responseData.expires_at * 1000).toISOString(),
      issued_at: new Date(responseData.issued_at * 1000).toISOString(),
      expires_in: responseData.expires_in
    });

    return {
      success: true,
      access_token: responseData.access_token,
      encrypted_access_token: responseData.encrypted_access_token,
      expires_in: responseData.expires_in,
      issued_at: responseData.issued_at,
      expires_at: responseData.expires_at,
      session_expires_at: responseData.session_expires_at,
      token_type: responseData.token_type
    };

  } catch (error) {
    console.error('[PhonePe] ❌ Authorization token generation failed:', error);
    if (error.response) {
      console.error('[PhonePe] Error response:', error.response.data);
      console.error('[PhonePe] Error status:', error.response.status);
      console.error('[PhonePe] Error headers:', error.response.headers);
    }
    throw error;
  }
};

/**
 * Generate SHA256 hash for PhonePe signature
 * @param {string} data - Data to hash
 * @returns {Promise<string>} - Hex hash
 */
async function generateSHA256(data) {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate PhonePe signature
 * @param {string} payload - Base64 encoded payload
 * @param {string} endpoint - API endpoint
 * @returns {Promise<string>} - Signature with salt index
 */
async function generateSignature(payload, endpoint) {
  const dataToSign = payload + endpoint + PHONEPE_CONFIG.CLIENT_SECRET;
  const hash = await generateSHA256(dataToSign);
  return `${hash}###1`; // Salt index is typically 1
}

/**
 * Create a new payment transaction using PhonePe v2 API
 * @param {Object} paymentData - Payment details
 * @param {number} paymentData.amount - Amount in rupees
 * @param {string} paymentData.phone - Mobile number
 * @param {string} paymentData.name - Donor name
 * @param {string} paymentData.email - Donor email
 * @param {string} paymentData.transactionId - Optional transaction ID
 * @returns {Promise<Object>} Payment response with redirect URL
 */
export const createPayment = async (paymentData) => {
  try {
    console.log('💳 [PhonePe] Starting payment creation...');
    console.log('💳 [PhonePe] Environment:', import.meta.env.PROD ? 'Production' : 'Development');
    console.log('💳 [PhonePe] Payment data:', {
      amount: paymentData.amount,
      merchantOrderId: paymentData.transactionId || 'Will be generated',
      hasAuthToken: !!authToken,
      tokenExpiry: tokenExpiry ? new Date(tokenExpiry).toISOString() : 'No token'
    });

    // Check if we have a valid auth token
    if (!authToken || (tokenExpiry && Date.now() >= tokenExpiry)) {
      console.log('🔄 [PhonePe] Token expired or missing, generating new token...');
      await generateAuthToken();
    }

    // Validate input
    if (!paymentData.amount || paymentData.amount <= 0) {
      throw new Error('Invalid amount');
    }

    // Generate merchant order ID if not provided
    const merchantOrderId = paymentData.transactionId || generateTransactionId();
    const amountInPaise = Math.round(paymentData.amount * 100);

    // Prepare request headers
    const requestHeaders = {
      "Content-Type": "application/json",
      "Authorization": `O-Bearer ${authToken}`
    };

    // Prepare request body using PhonePe v2 API structure
    const requestBody = {
      "merchantOrderId": merchantOrderId,
      "amount": amountInPaise,
      "expireAfter": 1200, // 20 minutes
      "metaInfo": {
        "udf1": paymentData.name || "Donor",
        "udf2": paymentData.email || "donor@example.com",
        "udf3": paymentData.phone || "",
        "udf4": "Donation",
        "udf5": paymentData.cause || "General Donation"
      },
      "paymentFlow": {
        "type": "PG_CHECKOUT",
        "message": "Thank you for your donation to UMMEED NGO",
        "merchantUrls": {
          "redirectUrl": `${PHONEPE_CONFIG.REDIRECT_URL}?merchantOrderId=${merchantOrderId}`
        }
      }
    };

    console.log('💳 [PhonePe] Making payment request...');
    console.log('💳 [PhonePe] Request details:', {
      url: PHONEPE_CONFIG.PAYMENT_URL,
      method: 'POST',
      hasAuthToken: !!authToken,
      redirectUrl: requestBody.paymentFlow.merchantUrls.redirectUrl
    });

    let response;
    
    if (import.meta.env.PROD) {
      // Use Cloudflare Worker (production)
      console.log('💳 [PhonePe] Using Cloudflare Worker for payment creation');
      response = await axios.post(PHONEPE_CONFIG.PAYMENT_URL, {
        authToken: authToken,
        paymentData: requestBody
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } else {
      // Use direct API with proxy (development)
      console.log('💳 [PhonePe] Using direct API with Vite proxy');
      response = await axios.post(PHONEPE_CONFIG.PAYMENT_URL, requestBody, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `O-Bearer ${authToken}`
        }
      });
    }

    console.log('[PhonePe] Payment API Response:', response.data);

    const responseData = response.data;

    // Handle different response cases
    if (responseData.code) {
      // Error response
      throw new Error(`${responseData.code}: ${responseData.message}`);
    }

    if (!responseData.orderId || !responseData.redirectUrl) {
      throw new Error('Invalid response from PhonePe API');
    }

    console.log('[PhonePe] ✅ Payment created successfully!');
    console.log('[PhonePe] Order details:', {
      orderId: responseData.orderId,
      state: responseData.state,
      expireAt: new Date(responseData.expireAt).toISOString(),
      redirectUrl: responseData.redirectUrl
    });

    return {
      success: true,
      orderId: responseData.orderId,
      state: responseData.state,
      expireAt: responseData.expireAt,
      redirectUrl: responseData.redirectUrl,
      merchantOrderId: merchantOrderId,
      amount: paymentData.amount
    };

  } catch (error) {
    console.error('[PhonePe] ❌ Payment creation error:', error);
    if (error.response) {
      console.error('[PhonePe] Error response:', error.response.data);
      console.error('[PhonePe] Error status:', error.response.status);
    }
    throw error;
  }
};

/**
 * Check payment status using PhonePe v2 API
 * @param {string} merchantOrderId - Merchant Order ID to check
 * @param {boolean} details - Whether to return all payment attempt details (default: true)
 * @param {boolean} errorContext - Whether to return error context for failed payments (default: true)
 * @returns {Promise<Object>} Payment status with detailed information
 */
export const checkPaymentStatus = async (merchantOrderId, details = true, errorContext = true) => {
  try {
    console.log('[PhonePe] Step 3: Checking Payment Status using v2 API...');
    console.log('[PhonePe] Merchant Order ID:', merchantOrderId);

    if (!merchantOrderId) {
      throw new Error('Merchant Order ID is required');
    }

    // Check if we have a valid auth token
    if (!authToken || (tokenExpiry && Date.now() >= tokenExpiry)) {
      console.log('🔄 [PhonePe] Token expired or missing, generating new token...');
      await generateAuthToken();
    }

    console.log('📊 [PhonePe] Making status check request...');
    console.log('📊 [PhonePe] Request details:', {
      merchantOrderId,
      details,
      errorContext,
      hasAuthToken: !!authToken
    });

    let response;
    
    if (import.meta.env.PROD) {
      // Use Cloudflare Worker (production)
      console.log('📊 [PhonePe] Using Cloudflare Worker for status check');
      const queryParams = new URLSearchParams({
        merchantOrderId,
        authToken,
        details: details.toString(),
        errorContext: errorContext.toString()
      });
      response = await axios.get(`${PHONEPE_CONFIG.STATUS_URL}?${queryParams}`);
    } else {
      // Use direct API with proxy (development)
      console.log('📊 [PhonePe] Using direct API with Vite proxy');
      const queryParams = new URLSearchParams({
        details: details.toString(),
        errorContext: errorContext.toString()
      });
      const requestUrl = `${PHONEPE_CONFIG.STATUS_URL}/${merchantOrderId}/status?${queryParams}`;
      response = await axios.get(requestUrl, {
        headers: {
          'Authorization': `O-Bearer ${authToken}`
        }
      });
    }

    const responseData = response.data;
    console.log('[PhonePe] Status API Response:', responseData);

    // Handle error responses as per documentation
    if (responseData.code) {
      throw new Error(`${responseData.code}: ${responseData.message}`);
    }

    // Validate required fields as per documentation
    if (!responseData.orderId || !responseData.state) {
      throw new Error('Invalid response from PhonePe API');
    }

    console.log('[PhonePe] ✅ Payment status retrieved successfully!');
    console.log('[PhonePe] Payment status details:', {
      orderId: responseData.orderId,
      state: responseData.state,
      amount: responseData.amount,
      paymentAttempts: responseData.paymentDetails?.length || 0
    });

    // Parse and format the response as per v2 API documentation
    const formattedResponse = {
      success: true,
      orderId: responseData.orderId,
      state: responseData.state,
      amount: responseData.amount,
      payableAmount: responseData.payableAmount,
      feeAmount: responseData.feeAmount,
      expireAt: responseData.expireAt,
      metaInfo: responseData.metaInfo,
      paymentDetails: responseData.paymentDetails || [],
      errorContext: responseData.errorContext,
      merchantOrderId: merchantOrderId,
      // Helper methods for easy status checking
      isCompleted: responseData.state === 'COMPLETED',
      isFailed: responseData.state === 'FAILED',
      isPending: responseData.state === 'PENDING',
      hasPaymentAttempts: (responseData.paymentDetails?.length || 0) > 0,
      latestPayment: responseData.paymentDetails?.[0] || null,
      allPayments: responseData.paymentDetails || []
    };

    return formattedResponse;

  } catch (error) {
    console.error('[PhonePe] ❌ Status check error:', error);
    if (error.response) {
      console.error('[PhonePe] Error response:', error.response.data);
      console.error('[PhonePe] Error status:', error.response.status);
    }
    throw error;
  }
};

/**
 * Generate a unique transaction ID
 * @returns {string} Transaction ID
 */
export const generateTransactionId = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 10).toUpperCase();
  return `TXN_${timestamp}_${random}`;
};

/**
 * Validate and normalize phone number (Indian format)
 * @param {string} phone - Phone number to validate
 * @returns {object} {isValid: boolean, normalizedPhone: string}
 */
export const validatePhone = (phone) => {
  if (!phone) return { isValid: false, normalizedPhone: '' };
  
  const cleanPhone = phone.replace(/\D/g, '');
  
  // Handle different formats
  if (cleanPhone.length === 10) {
    // Standard 10-digit number
    if (/^[6-9]/.test(cleanPhone)) {
      return { isValid: true, normalizedPhone: cleanPhone };
    }
  } else if (cleanPhone.length === 11 && cleanPhone.startsWith('0')) {
    // 11-digit number starting with 0
    const withoutZero = cleanPhone.substring(1);
    if (/^[6-9]/.test(withoutZero)) {
      return { isValid: true, normalizedPhone: withoutZero };
    }
  } else if (cleanPhone.length === 12 && cleanPhone.startsWith('91')) {
    // 12-digit number with country code
    const withoutCountryCode = cleanPhone.substring(2);
    if (/^[6-9]/.test(withoutCountryCode)) {
      return { isValid: true, normalizedPhone: withoutCountryCode };
    }
  }
  
  return { isValid: false, normalizedPhone: cleanPhone };
};

/**
 * Validate email
 * @param {string} email - Email to validate
 * @returns {boolean} Is valid
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Format amount with commas for Indian currency
 * @param {number} amount - Amount to format
 * @returns {string} Formatted amount
 */
export const formatAmount = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Handle payment success callback
 * @param {string} transactionId - Transaction ID from URL
 * @returns {Promise<Object>} Payment details
 */
export const handlePaymentSuccess = async (transactionId) => {
  try {
    console.log('[PhonePe] Handling payment success for:', transactionId);
    
    // Check payment status
    const statusResult = await checkPaymentStatus(transactionId);
    
    return {
      success: true,
      transactionId: transactionId,
      status: statusResult.status,
      amount: statusResult.amount,
      data: statusResult.data,
    };
  } catch (error) {
    console.error('[PhonePe] Payment success handling error:', error);
    throw error;
  }
};

/**
 * Get payment status from URL parameters
 * @returns {Object} Payment status info
 */
export const getPaymentStatusFromURL = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const transactionId = urlParams.get('transactionId');
  const status = urlParams.get('status');
  const amount = urlParams.get('amount');
  
  return {
    transactionId,
    status,
    amount: amount ? parseFloat(amount) : null,
    hasTransactionId: !!transactionId,
  };
};

/**
 * Complete donation flow - generates token and creates payment
 * This is the main function to call when user clicks donate
 * @param {Object} paymentData - Payment details
 * @returns {Promise<Object>} Payment response
 */
export const initiateDonation = async (paymentData) => {
  try {
    console.log('[PhonePe] 🚀 Starting complete donation flow...');
    console.log('[PhonePe] Payment data:', paymentData);

    // Step 1: Generate Authorization Token
    console.log('[PhonePe] ===========================================');
    console.log('[PhonePe] STEP 1: AUTHORIZATION TOKEN GENERATION');
    console.log('[PhonePe] ===========================================');
    
    const tokenResult = await generateAuthToken();
    
    if (!tokenResult.success) {
      throw new Error('Failed to generate authorization token');
    }

    // Step 2: Create Payment
    console.log('[PhonePe] ===========================================');
    console.log('[PhonePe] STEP 2: PAYMENT CREATION');
    console.log('[PhonePe] ===========================================');
    
    const paymentResult = await createPayment(paymentData);
    
    if (!paymentResult.success) {
      throw new Error('Failed to create payment');
    }

    console.log('[PhonePe] ===========================================');
    console.log('[PhonePe] ✅ DONATION FLOW COMPLETED SUCCESSFULLY!');
    console.log('[PhonePe] ===========================================');
    console.log('[PhonePe] Transaction ID:', paymentResult.transactionId);
    console.log('[PhonePe] Redirect URL:', paymentResult.redirectUrl);

    return paymentResult;

  } catch (error) {
    console.error('[PhonePe] ❌ Donation flow failed:', error);
    throw error;
  }
};

// Default export
export default {
  checkPhonePeConfiguration,
  generateAuthToken,
  createPayment,
  checkPaymentStatus,
  generateTransactionId,
  validatePhone,
  validateEmail,
  formatAmount,
  handlePaymentSuccess,
  getPaymentStatusFromURL,
  initiateDonation,
};
